/**
 * PHP API ユーティリティ
 *
 * XServerのnginxはContent-Type: application/jsonのPOSTでプリフライトを横取りしてCORSエラーになる。
 * このユーティリティはFormData方式を強制し、CORSエラーを防ぐ。
 */

import { appendIdToken } from '@/js/utils/firebaseAuth'

const PHP_API_BASE = process.env.VUE_APP_PHP_API_BASE || '/oh3/php'

/** VPS API ベースURL（shorturl・sessionState用） */
export const VPS_API_BASE = process.env.VUE_APP_VPS_API_BASE || 'https://kenzkenz.net/myphp'

/** VPS Node.js APIベースURL（Express /api/* エンドポイント用） */
export const VPS_NODE_BASE = process.env.VUE_APP_VPS_NODE_BASE || 'https://kenzkenz.net/api'

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
type ApiData = Record<string, JsonValue | undefined>

/**
 * PHP APIにPOSTリクエストを送信（FormData方式）
 *
 * @param endpoint - エンドポイント名（例: 'claudeOcrCorrection.php'）
 * @param data - 送信するデータ（オブジェクトや配列はJSON文字列化される）
 * @returns レスポンスJSON
 *
 * @example
 * // 単純な値
 * const result = await phpApiPost('example.php', { name: 'test', count: 10 })
 *
 * // 複雑なオブジェクト（自動的にJSON文字列化される）
 * const result = await phpApiPost('claudeOcrCorrection.php', {
 *   tables: [{ headers: [...], rows: [...] }],  // 配列→JSON文字列
 *   image: 'base64...',                          // 文字列→そのまま
 *   mimeType: 'image/png'
 * })
 */
export async function phpApiPost<T = unknown>(endpoint: string, data: ApiData = {}): Promise<T> {
  const url = `${PHP_API_BASE}/${endpoint}`
  const formData = new FormData()

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue

    // オブジェクトや配列はJSON文字列化
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value))
    } else {
      formData.append(key, String(value))
    }
  }

  // IDトークン認証を自動付与
  await appendIdToken(formData)

  const response = await fetch(url, {
    method: 'POST',
    body: formData
  })

  return response.json() as Promise<T>
}

/**
 * PHP APIにGETリクエストを送信
 *
 * @param endpoint - エンドポイント名
 * @param params - クエリパラメータ
 * @returns レスポンスJSON
 */
export async function phpApiGet<T = unknown>(endpoint: string, params: Record<string, string | number | boolean | null | undefined> = {}): Promise<T> {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value))
    }
  }
  const qs = searchParams.toString()
  const url = `${PHP_API_BASE}/${endpoint}${qs ? '?' + qs : ''}`

  const response = await fetch(url)
  return response.json() as Promise<T>
}

export default {
  post: phpApiPost,
  get: phpApiGet,
  baseUrl: PHP_API_BASE
}
